import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", rating: 5, comment: { fr: "", nl: "", en: "" }, avatar_url: "", vehicle_bought: "" };

export default function AdminTestimonials() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      if (form.id) await api.put(`/testimonials/${form.id}`, form);
      else await api.post("/testimonials", form);
      toast.success("Enregistré"); setOpen(false); load();
    } catch (e) {
      console.error("save testimonial error:", e, e?.response?.data);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.[0]?.msg) || "Enregistrement impossible");
    }
  };
  const del = async (id) => { if (window.confirm(t.admin.confirm_delete)) { await api.delete(`/testimonials/${id}`); load(); } };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl tracking-tight font-light text-white">Témoignages</h1>
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">
          <Plus className="w-4 h-4 mr-2" /> {t.admin.new}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-[#1E1E1E] border border-white/10 rounded-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-1">
                {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-[#C8A75A]" fill="#C8A75A" />)}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ ...empty, ...r, comment: { fr:"", nl:"", en:"", ...r.comment }}); setOpen(true); }} className="text-white/60 hover:text-[#C8A75A] p-1"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-white/60 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <p className="text-white/70 text-sm italic mb-4 line-clamp-3">« {r.comment?.fr || r.comment?.en} »</p>
            <p className="text-white text-sm">{r.name}</p>
            <p className="text-white/40 text-xs">{r.vehicle_bought}</p>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full text-white/40 text-center py-8">Aucun témoignage.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Modifier" : "Nouveau"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Input placeholder="Nom" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0D0D0D] border-white/10" />
            <Input type="number" min={1} max={5} placeholder="Note (1-5)" value={form.rating} onChange={(e) => set("rating", Number(e.target.value))} className="bg-[#0D0D0D] border-white/10" />
            <Input placeholder="Véhicule acheté" value={form.vehicle_bought} onChange={(e) => set("vehicle_bought", e.target.value)} className="bg-[#0D0D0D] border-white/10" />
            <Textarea rows={3} placeholder="Commentaire FR" value={form.comment.fr} onChange={(e) => set("comment", { ...form.comment, fr: e.target.value })} className="bg-[#0D0D0D] border-white/10" />
            <Textarea rows={3} placeholder="Commentaire NL" value={form.comment.nl} onChange={(e) => set("comment", { ...form.comment, nl: e.target.value })} className="bg-[#0D0D0D] border-white/10" />
            <Textarea rows={3} placeholder="Commentaire EN" value={form.comment.en} onChange={(e) => set("comment", { ...form.comment, en: e.target.value })} className="bg-[#0D0D0D] border-white/10" />
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/20 text-white hover:bg-white/5">{t.admin.cancel}</Button>
            <Button onClick={save} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">{t.admin.save}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
